import { ComponentRef, Injectable, inject } from '@angular/core';
import { TimeBlockComponent } from '../time-block.component';
import { CalendarService } from '../../../services/calendar.service';
import { ITimeBlockComponentItem } from '../types/time-block-component-item-types';
import { TimeBlockItemBuilderService } from './time-block-item-builder.service';
import { CompoundTimeBlockViewType, TimeBlockHookContainer } from '../types/time-block-types';
import { DayOrWeekCalendarModel } from '../../../../../core/models/calendar/day-or-week-calendar.model';
import {
  TimeBlockDayOrWeekFixIntervalType,
  TimeBlockDayOrWeekVariableIntervalType,
  TimeBlockMonthFixIntervalType,
  TimeBlockMonthVariableIntervalType,
} from '../../../../../core/models/timeblock/time-block-view-type.model';
import { MonthCalendarModel } from '../../../../../core/models/calendar/month-calendar.model';
import { DateTimeHelper } from '../../../util/date-time-helper';
import { CalendarViews } from '../../../calendar-types';

@Injectable()
export class TimeBlockComponentHandlerService {
  private readonly calendarService = inject(CalendarService);
  private readonly timeBlockItemBuilderService = inject(TimeBlockItemBuilderService);
  private timeBlockComponentBuilder: TimeBlockComponentFactory;

  public get TimeBlockComponentBuilder(): TimeBlockComponentFactory {
    if (!this.timeBlockComponentBuilder) {
      this.timeBlockComponentBuilder = new TimeBlockComponentFactory(
        this.timeBlockItemBuilderService,
        this.calendarService,
      );
    }
    return this.timeBlockComponentBuilder;
  }

  /**
   * Don't call this method directly.
   * Use the updateInCalendar() method of the time block or the TimeBlockRenderService instead.
   */
  public destroyComponent(comp: ITimeBlockComponentItem): void {
    if (!comp.timeBlockModel.componentRef) {
      return;
    }
    const componentRef = comp.timeBlockModel.componentRef;
    componentRef.destroy();
    componentRef.instance.cd.detectChanges();
  }
}

export interface ITimeBlockComponentBuilder {
  buildComponent: (comp: ITimeBlockComponentItem) => ComponentRef<TimeBlockComponent>;
}

class TimeBlockComponentFactory implements ITimeBlockComponentBuilder {
  constructor(
    private readonly timeBlockItemBuilderService: TimeBlockItemBuilderService,
    private readonly calService: CalendarService,
  ) {}

  public buildComponent(timeBlock: ITimeBlockComponentItem): ComponentRef<TimeBlockComponent> {
    const [start, end] = this.timeBlockItemBuilderService.getTimeBlockStartAndEndDates(timeBlock);
    timeBlock.timeBlockModel.timeBlockViewType = this.updateTimeBlockViewType(
      timeBlock,
      start,
      end,
    );

    // If this time block is a fix lane time block in the day, week or month view, don't render non-head parts.
    if (this.isHiddenFixIntervalTimeBlockPart(timeBlock)) {
      // Also remove the component ref.
      timeBlock.timeBlockModel.componentRef = null;
      return;
    }

    const activeViewContainer = this.getViewContainer(timeBlock);

    if (!activeViewContainer) {
      throw new Error(`View container is undefined for time block ${timeBlock}`);
    }
    const componentRef = activeViewContainer.vcr.createComponent(TimeBlockComponent);
    timeBlock.timeBlockModel.componentRef = componentRef;

    // / Clone the original model and assign it
    const clonedComponent = timeBlock.clone(this.timeBlockItemBuilderService);
    timeBlock.timeBlockModel = clonedComponent.timeBlockModel;
    componentRef.instance.timeBlockComponentItem = timeBlock;

    // Add component to the view container
    activeViewContainer.vcr.insert(componentRef.hostView);
    activeViewContainer.cd.detectChanges();

    return componentRef;
  }

  /**
   * Check if time block part is a full day time block part and is not visible in the calendar view.
   */
  public isHiddenFixIntervalTimeBlockPart(timeBlockComponent: ITimeBlockComponentItem): boolean {
    const timeBlockModel = timeBlockComponent.timeBlockModel;
    const calendarViewType = this.calService.model.calendarViewMode;
    const calendarModel = this.calService.model;

    if (
      timeBlockModel.fixInterval !== null &&
      calendarViewType === CalendarViews.WeekGrid &&
      timeBlockModel.partNumber > 0
    ) {
      return true;
    } else if (
      timeBlockModel.fixInterval !== null &&
      calendarViewType === CalendarViews.MonthGrid
    ) {
      const timeBlockHeadDate = DateTimeHelper.isBefore(
        timeBlockModel.start,
        calendarModel.calendarProperties.visibleStartDate,
      )
        ? calendarModel.calendarProperties.visibleStartDate
        : timeBlockModel.start;
      const currentDate = DateTimeHelper.addDays(timeBlockHeadDate, timeBlockModel.partNumber);

      const currentStartOfWeek = DateTimeHelper.startOfWeek(currentDate, {
        weekStartsOn: calendarModel.calendarProperties.weekStartsOn,
      });

      const start = DateTimeHelper.isBefore(timeBlockModel.start, currentStartOfWeek)
        ? currentStartOfWeek
        : timeBlockModel.start;
      return !DateTimeHelper.isSameDay(start, currentDate);
    }
    return false;
  }

  private getViewContainer(timeBlock: ITimeBlockComponentItem): TimeBlockHookContainer {
    const timeBlockViewType = timeBlock.timeBlockModel.timeBlockViewType;
    const calendarModel = this.calService.model as DayOrWeekCalendarModel | MonthCalendarModel;

    if (
      timeBlockViewType instanceof TimeBlockDayOrWeekFixIntervalType ||
      timeBlockViewType instanceof TimeBlockMonthFixIntervalType
    ) {
      const containerIndex = this.calService.getContainerIndexByTimeBlock(timeBlock);
      return calendarModel.fixIntervalCalendarModel.timeBlockContainers[containerIndex];
    } else if (timeBlockViewType instanceof TimeBlockDayOrWeekVariableIntervalType) {
      const containerIndex = this.calService.getContainerIndexByTimeBlock(timeBlock);
      return calendarModel.timeBlockContainers[containerIndex];
    }
    const containerIndex = this.calService.getContainerIndexByTimeBlock(timeBlock);
    return calendarModel.timeBlockContainers[containerIndex];
  }

  private updateTimeBlockViewType(
    timeBlockComponentItem: ITimeBlockComponentItem,
    timeBlockStart: Date,
    timeBlockEnd: Date,
  ): CompoundTimeBlockViewType {
    const tbModel = timeBlockComponentItem.timeBlockModel;
    let updatedTimeBlockViewType: CompoundTimeBlockViewType;
    const calendarOffsetStart = this.calService.model.calendarProperties.offsetStartDate;

    if (
      tbModel.timeBlockViewType instanceof TimeBlockDayOrWeekFixIntervalType ||
      tbModel.timeBlockViewType instanceof TimeBlockMonthFixIntervalType
    ) {
      updatedTimeBlockViewType =
        tbModel.timeBlockViewType instanceof TimeBlockDayOrWeekFixIntervalType
          ? new TimeBlockDayOrWeekFixIntervalType()
          : new TimeBlockMonthFixIntervalType();
      Object.assign(updatedTimeBlockViewType, tbModel.timeBlockViewType);
      updatedTimeBlockViewType.laneIndexStart = DateTimeHelper.differenceInDays(
        timeBlockStart,
        calendarOffsetStart,
      );
      updatedTimeBlockViewType.laneIndexEnd = DateTimeHelper.differenceInDays(
        timeBlockEnd,
        calendarOffsetStart,
      );
    } else if (
      tbModel.timeBlockViewType instanceof TimeBlockDayOrWeekVariableIntervalType ||
      tbModel.timeBlockViewType instanceof TimeBlockMonthVariableIntervalType
    ) {
      updatedTimeBlockViewType =
        tbModel.timeBlockViewType instanceof TimeBlockDayOrWeekVariableIntervalType
          ? new TimeBlockDayOrWeekVariableIntervalType()
          : new TimeBlockMonthVariableIntervalType();
      Object.assign(updatedTimeBlockViewType, tbModel.timeBlockViewType);
      const timeBlockPartDate = DateTimeHelper.addDays(timeBlockStart, tbModel.partNumber);
      updatedTimeBlockViewType.laneIndex = DateTimeHelper.differenceInDays(
        timeBlockPartDate,
        calendarOffsetStart,
      );
    } else {
      throw new Error('Time block view type not supported.');
    }

    return updatedTimeBlockViewType;
  }
}
